import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutation, useMutationState, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Info, Waypoints } from "lucide-react";
import { CaretUpIcon } from "@phosphor-icons/react";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@libs/zapi-ui/shadcn";
import $api from "@/zapi-frontend/shared/api/axios";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { useMemoViewStore } from "@/zapi-frontend/stores/memoView";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { ChapterGeneratingSkeleton } from "../GenerationStates/ChapterGeneratingSkeleton";
import { ChapterQueuedScreen } from "../GenerationStates/ChapterQueuedScreen";
import { ProjectLoadingScreen } from "../ProjectLoadingScreen";
import { TChainAnnotationKind, TChainOwner, TChainPositionKind, TValueChain } from "./chainTypes";

const ANNOTATION_KEY = {
  concentration: "valueChain.annotation.concentration",
  rivalry: "valueChain.annotation.rivalry",
  "input-difficulty": "valueChain.annotation.inputDifficulty",
  "value-capture": "valueChain.annotation.valueCapture"
} as const satisfies Record<TChainAnnotationKind, string>;

const POSITION_KEY = {
  base: "valueChain.position.base",
  candidate: "valueChain.position.candidate",
  recommended: "valueChain.position.recommended",
  rejected: "valueChain.position.rejected"
} as const satisfies Record<TChainPositionKind, string>;

// Backend error responses carry a stable `code`; the raw message is English-only, so the
// UI maps the code to a localized string and never renders the server text directly.
const ERROR_CODE_KEY = {
  "value-chain/not-enabled": "valueChain.errors.notEnabled",
  "value-chain/in-progress": "valueChain.errors.inProgress",
  "value-chain/no-content": "valueChain.errors.noContent",
  "value-chain/generation-failed": "valueChain.errors.generationFailed"
} as const;


// Figma 16894-40545: 32px circle over the fade, 6px from the edge of the scroller.
const NAV_BUTTON =
  "absolute top-1/2 z-20 flex size-8 -translate-y-1/2 items-center justify-center rounded-full border border-innoveraZinc-150 bg-white shadow-[0px_2px_8px_0px_rgba(0,0,0,0.07)] hover:bg-innoveraZinc-50";

// Matches GENERATION_LOCK_TTL_SECONDS on the backend: a run that is still going is not
// a failure, and reporting it as one sends users into a 409 on the retry.
const GENERATE_TIMEOUT_MS = 1500 * 1000;

type TValueChainResponse = {
  chain: TValueChain | null;
  generatedAt: string | null;
  generationStatus: "pending" | "processing" | null;
};


// Annotations are verbatim quotes and often start mid-sentence; the design shows them
// sentence-cased. Containment on the backend compares case-insensitively, so this is safe.
function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const VALUE_CHAIN_KEY = "project-value-chain";
const VALUE_CHAIN_GENERATE_KEY = "project-value-chain-generate";

export function ValueChainIsland() {
  const { id: projectId } = useParams();
  const { t } = useTranslation("projectHome");
  const queryClient = useQueryClient();
  const { role, accessMode } = useMeStore();
  const canGenerate = hasEditAccess(role, accessMode);
  const isSetupActive = useProjectSetupStore((s) => !!s.activeSetupTasks[projectId ?? ""]);
  const isFullWidth = useMemoViewStore((s) => s.isFullWidth);

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: [VALUE_CHAIN_KEY, projectId],
    queryFn: async () => {
      const res = await $api.get(`/projects/${projectId}/value-chain`);
      return res.data.data as TValueChainResponse;
    },
    enabled: !!projectId,
    // The automatic run happens on the server after project setup, so the page has to keep
    // asking while a task is queued or running to swap the skeleton for the chain.
    refetchInterval: (query) => (query.state.data?.generationStatus ? 15000 : false)
  });

  // Keyed and read back through the client: the run has to survive leaving the tab.
  // A mutation kept only in component state dies with the unmount, and the user comes
  // back to an empty state with a Generate button while the request is still in flight.
  const generateMutation = useMutation({
    mutationKey: [VALUE_CHAIN_GENERATE_KEY, projectId],
    mutationFn: async (_hadChain: boolean) => {
      const res = await $api.post(`/projects/${projectId}/value-chain/generate`, {}, { timeout: GENERATE_TIMEOUT_MS });
      return res.data.data as TValueChainResponse;
    },
    onSuccess: (fresh, hadChain) => {
      queryClient.setQueryData([VALUE_CHAIN_KEY, projectId], fresh);
      toast.success(hadChain ? t("valueChain.regeneratedToast") : t("valueChain.generatedToast"));
    }
  });

  const runningStatuses = useMutationState({
    filters: { mutationKey: [VALUE_CHAIN_GENERATE_KEY, projectId], status: "pending" },
    select: (mutation) => mutation.state.status
  });
  const isGenerating =
    generateMutation.isPending || runningStatuses.length > 0 || data?.generationStatus === "processing";

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <ProjectLoadingScreen />
      </div>
    );
  }

  const chain = data?.chain;
  const isQueued = (data?.generationStatus === "pending" || (isSetupActive && !chain)) && !isGenerating;

  // Chapters render their queued/generating states full-height and centered; this page
  // does the same instead of tucking them under its own header.
  if (isQueued && !chain) {
    return (
      <div className="h-full bg-white">
        <ChapterQueuedScreen title={t("sidebar.valueChain")} pills={[]} />
      </div>
    );
  }

  if (isGenerating && !chain) {
    return (
      <div className="h-full overflow-y-auto bg-white">
        <ChapterGeneratingSkeleton />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-white px-4 laptop:px-7">
      <div className={cn("mx-auto flex w-full min-w-0 flex-col gap-5 py-10", isFullWidth ? "max-w-none" : "max-w-[725px]")}>
        <section className="flex flex-col gap-[3px]">
          <div className="flex items-start justify-between gap-6">
            <FigmaTypography component="h1" variant="memo-heading-1" color="zinc900">
              {chain?.label ?? t("valueChain.title")}
            </FigmaTypography>
            {canGenerate && (
              <button
                type="button"
                disabled={isGenerating || isQueued}
                onClick={() => generateMutation.mutate(!!chain)}
                className="inline-flex shrink-0 items-center gap-[7px] whitespace-nowrap rounded-md border border-innoveraZinc-200 bg-white px-3.5 py-2 text-[13.5px] font-medium text-innoveraZinc-900 hover:bg-innoveraZinc-50 disabled:opacity-60"
              >
                <span
                  className={cn("h-1.5 w-1.5 rounded-full bg-innoveraOrange-400", isGenerating && "animate-pulse")}
                />
                {isGenerating
                  ? t("valueChain.generating")
                  : chain
                    ? t("valueChain.regenerate")
                    : t("valueChain.generate")}
              </button>
            )}
          </div>
          {chain && (
            <FigmaTypography variant="memo-body-regular" color="zinc900">
              {chain.intent}
            </FigmaTypography>
          )}
          {generateMutation.isError && (
            <FigmaTypography variant="body-2" className="text-innoveraRed-400">
              {(() => {
                const code = (generateMutation.error as { response?: { data?: { code?: string } } })?.response?.data
                  ?.code;
                const key = code ? ERROR_CODE_KEY[code as keyof typeof ERROR_CODE_KEY] : undefined;
                return key ? t(key) : t("valueChain.generationFailed");
              })()}
            </FigmaTypography>
          )}
        </section>

        {isError && !chain && !isGenerating && (
          <section className="flex flex-col items-center gap-3 rounded border border-innoveraZinc-150 px-6 py-16">
            <Waypoints className="h-8 w-8 text-innoveraZinc-300" />
            <FigmaTypography variant="heading-3" color="zinc900">
              {t("valueChain.loadErrorTitle")}
            </FigmaTypography>
            <FigmaTypography variant="body-2" color="zinc500" className="max-w-[380px] text-center">
              {t("valueChain.loadErrorBody")}
            </FigmaTypography>
            <AppButton
              appearance="outlined"
              color="zinc150"
              className="mt-1 text-innoveraZinc-900 shadow-none"
              onClick={() => refetch()}
              isLoading={isFetching}
            >
              {t("valueChain.retry")}
            </AppButton>
          </section>
        )}

        {!chain && !isError && !isGenerating && !isQueued && (
          <section className="flex flex-col items-center gap-3 rounded border border-innoveraZinc-150 px-6 py-16">
            <Waypoints className="h-8 w-8 text-innoveraZinc-300" />
            <FigmaTypography variant="heading-3" color="zinc900">
              {t("valueChain.emptyTitle")}
            </FigmaTypography>
            <FigmaTypography variant="body-2" color="zinc500" className="max-w-[380px] text-center">
              {canGenerate ? t("valueChain.emptyBodyEditor") : t("valueChain.emptyBodyViewer")}
            </FigmaTypography>
          </section>
        )}

        {chain && (
          <>
            <ChainCanvas chain={chain} />
            <WhoHoldsSection chain={chain} />
            {!!chain.narrative?.length && (
              <section className="flex flex-col gap-[11px] border-t border-innoveraZinc-150 pt-3">
                {chain.narrative.map((paragraph, i) => (
                  <FigmaTypography key={i} component="p" variant="memo-body-regular" color="zinc900">
                    {paragraph}
                  </FigmaTypography>
                ))}
              </section>
            )}
            <p className="mx-1 mb-3 mt-1.5 max-w-[780px] text-[12px] leading-[1.6] text-innoveraZinc-400">
              {t("valueChain.footerNote")}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// Figma: Value Chain / Product 16832-99904. Card 255x413, 12px gutter, base position
// drawn as a zinc-100 frame around its stages, the rest as ruled labels underneath.
const CARD_WIDTH = 255;
const CARD_GAP = 12;

const TINT_BASE = "linear-gradient(0deg, rgb(250,250,250) 0%, rgba(250,250,250,0) 52%)";
const TINT_FOCUS = `linear-gradient(0deg, rgb(235,240,252) 0%, rgba(235,240,252,0) 52%), ${TINT_BASE}`;
const CARD_SHADOW = "0px 7px 12px 0px rgba(24,24,27,0.03)";

const OWNER_KEY = {
  ours: "valueChain.owner.ours",
  suppliers: "valueChain.owner.suppliers",
  customers: "valueChain.owner.customers",
  others: "valueChain.owner.others",
  nobody: "valueChain.owner.nobody"
} as const satisfies Record<TChainOwner, string>;

function StageCard({
  stage,
  index,
  total,
  focused,
  inBase,
  heightPx,
  headerPx,
  cardRef,
  headerRef
}: {
  stage: TValueChain["stages"][number];
  index: number;
  total: number;
  focused: boolean;
  inBase: boolean;
  heightPx: number | null;
  headerPx: number | null;
  cardRef: (el: HTMLDivElement | null) => void;
  headerRef: (el: HTMLDivElement | null) => void;
}) {
  const { t } = useTranslation("projectHome");
  // Focused cards layer the blue tint over the gray one; base-case cards are plain white.
  const tint = focused ? TINT_FOCUS : inBase ? undefined : TINT_BASE;

  return (
    <div
      ref={cardRef}
      className="relative flex min-h-[413px] shrink-0 items-start border border-innoveraZinc-150 bg-white p-4"
      style={{
        width: CARD_WIDTH,
        height: heightPx ?? undefined,
        backgroundImage: tint,
        boxShadow: focused || inBase ? CARD_SHADOW : undefined
      }}
    >
      {focused && <span className="absolute inset-x-0 top-0 h-[2.5px] bg-[#1f3e89]" />}
      <div className="flex flex-1 flex-col gap-4">
        <div ref={headerRef} className="flex min-h-[164px] flex-col gap-3" style={{ height: headerPx ?? undefined }}>
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center rounded-2xl border border-innoveraZinc-150 px-2.5 py-1">
              <FigmaTypography variant="label-3" color="zinc900">
                {t(OWNER_KEY[stage.owner])}
              </FigmaTypography>
            </span>
            <FigmaTypography variant="label-3" color="zinc400">
              {`${index + 1}/${total}`}
            </FigmaTypography>
          </div>
          <div className="flex flex-col gap-1.5">
            <FigmaTypography variant="heading-3" color="zinc900">
              {stage.label}
            </FigmaTypography>
            {stage.heldBy && (
              <FigmaTypography variant="body-2" color="zinc500">
                {stage.heldBy}
              </FigmaTypography>
            )}
          </div>
        </div>

        {!!stage.annotations?.length && (
          <>
            <span className="h-px w-full bg-innoveraZinc-150" />
            <div className="flex flex-col gap-4">
              {stage.annotations.map((annotation, i) => (
                <div key={i} className="flex flex-col gap-2">
                  <FigmaTypography variant="caption-1" color="zinc500">
                    {t(ANNOTATION_KEY[annotation.kind])}
                  </FigmaTypography>
                  <FigmaTypography variant="body-2" color="zinc900">
                    {sentenceCase(annotation.text)}
                  </FigmaTypography>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PositionRule({
  position,
  width
}: {
  position: TValueChain["positions"][number];
  width: number;
}) {
  const { t } = useTranslation("projectHome");
  const dashed = position.kind === "rejected";
  const line = cn("h-px flex-1", dashed ? "border-t border-dashed border-innoveraZinc-300" : "bg-innoveraZinc-900");

  return (
    <div className="flex flex-col" style={{ width }}>
      <div className="flex items-center">
        <span className={cn("h-3 w-px", dashed ? "bg-innoveraZinc-300" : "bg-innoveraZinc-900")} />
        <div className="flex flex-1 items-center justify-center gap-2">
          <span className={line} />
          <FigmaTypography variant="label-2" color="zinc900" className="whitespace-nowrap">
            {t(POSITION_KEY[position.kind])}
          </FigmaTypography>
          <span className={line} />
        </div>
        <span className={cn("h-3 w-px", dashed ? "bg-innoveraZinc-300" : "bg-innoveraZinc-900")} />
      </div>
      <div className="mt-2 flex items-center justify-center gap-1 px-3">
        <FigmaTypography variant="body-2" color="zinc500" className="text-center">
          {position.label}
        </FigmaTypography>
        {position.note && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={t("valueChain.aboutPosition", { label: position.label })}
                className="shrink-0 text-innoveraZinc-400"
              >
                <Info className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-[260px] text-sm leading-[22px] text-innoveraZinc-700">
              {position.note}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

function ChainCanvas({ chain }: { chain: TValueChain }) {
  const { t } = useTranslation("projectHome");
  const order = chain.stages.map((s) => s.key);
  const total = chain.stages.length;
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const syncEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdges({ left: el.scrollLeft > 1, right: el.scrollLeft < max - 1 });
  }, []);

  const scrollBy = (direction: 1 | -1) =>
    scrollerRef.current?.scrollBy({ left: direction * (CARD_WIDTH + CARD_GAP), behavior: "smooth" });

  const spanOf = (position: TValueChain["positions"][number]) => {
    const at = position.stages
      .map((k) => order.indexOf(k))
      .filter((i) => i >= 0)
      .sort((a, b) => a - b);
    return at.length ? { from: at[0], to: at[at.length - 1] } : null;
  };

  const base = chain.positions.find((p) => p.kind === "base");
  const baseSpan = base ? spanOf(base) : null;
  const recommended = chain.positions.find((p) => p.kind === "recommended");
  const recommendedSpan = recommended ? spanOf(recommended) : null;
  // Overlapping runs cannot share a row: their labels sit mid-rule and would collide.
  const ruleRows: { position: TValueChain["positions"][number]; span: { from: number; to: number } }[][] = [];
  for (const position of chain.positions) {
    if (position.kind === "base") continue;
    const span = spanOf(position);
    if (!span) continue;
    const row = ruleRows.find((entries) => entries.every((e) => e.span.to < span.from || e.span.from > span.to));
    if (row) row.push({ position, span });
    else ruleRows.push([{ position, span }]);
  }

  // The base group has its own padding and a tighter card gap, so rule positions are
  // measured from the rendered cards instead of derived arithmetically.
  const cardEls = useRef<(HTMLDivElement | null)[]>([]);
  const cardsRowRef = useRef<HTMLDivElement>(null);
  const [stagePx, setStagePx] = useState<{ left: number; width: number }[]>([]);
  // The design fixes cards at 413px; our content can be taller, so every card gets the
  // height of the tallest one — otherwise the bottom-aligned rows go ragged at the top.
  const [cardHeight, setCardHeight] = useState<number | null>(null);
  // Header blocks (badge + title + heldBy) also stretch to the tallest one, so the
  // divider under them sits on the same level in every card.
  const headerEls = useRef<(HTMLDivElement | null)[]>([]);
  const [headerHeight, setHeaderHeight] = useState<number | null>(null);

  const measureStages = useCallback(() => {
    const originEl = cardsRowRef.current;
    if (!originEl) return;
    const origin = originEl.getBoundingClientRect().left;
    setStagePx(
      chain.stages.map((_, i) => {
        const el = cardEls.current[i];
        if (!el) return { left: i * (CARD_WIDTH + CARD_GAP), width: CARD_WIDTH };
        const r = el.getBoundingClientRect();
        return { left: r.left - origin, width: r.width };
      })
    );
  }, [chain.stages]);

  useLayoutEffect(() => {
    setHeaderHeight(null);
    setCardHeight(null);
  }, [chain]);

  useLayoutEffect(() => {
    if (headerHeight !== null) return;
    const natural = headerEls.current.filter(Boolean).map((el) => (el as HTMLDivElement).offsetHeight);
    if (natural.length) setHeaderHeight(Math.max(164, ...natural));
  }, [headerHeight]);

  useLayoutEffect(() => {
    if (headerHeight === null || cardHeight !== null) return;
    const natural = cardEls.current.filter(Boolean).map((el) => (el as HTMLDivElement).offsetHeight);
    if (natural.length) setCardHeight(Math.max(413, ...natural));
  }, [headerHeight, cardHeight]);

  useEffect(() => {
    measureStages();
    syncEdges();
    window.addEventListener("resize", measureStages);
    window.addEventListener("resize", syncEdges);
    return () => {
      window.removeEventListener("resize", measureStages);
      window.removeEventListener("resize", syncEdges);
    };
  }, [measureStages, syncEdges, cardHeight]);

  const preStages = baseSpan ? chain.stages.slice(0, baseSpan.from) : chain.stages;
  const baseStages = baseSpan ? chain.stages.slice(baseSpan.from, baseSpan.to + 1) : [];
  const postStages = baseSpan ? chain.stages.slice(baseSpan.to + 1) : [];

  const renderCard = (stage: TValueChain["stages"][number], i: number, inBase: boolean) => (
    <StageCard
      key={stage.key}
      stage={stage}
      index={i}
      total={total}
      focused={!!recommendedSpan && i >= recommendedSpan.from && i <= recommendedSpan.to}
      inBase={inBase}
      heightPx={cardHeight}
      headerPx={headerHeight}
      cardRef={(el) => {
        cardEls.current[i] = el;
      }}
      headerRef={(el) => {
        headerEls.current[i] = el;
      }}
    />
  );

  return (
    <section className="relative min-w-0">
      {edges.left && (
        <>
          <span className="pointer-events-none absolute inset-y-0 left-0 z-10 w-[60px] bg-gradient-to-l from-transparent to-white" />
          <button
            type="button"
            aria-label={t("valueChain.scrollEarlier")}
            onClick={() => scrollBy(-1)}
            className={NAV_BUTTON}
            style={{ left: 6 }}
          >
            <ChevronLeft className="h-4 w-4 text-innoveraZinc-900" />
          </button>
        </>
      )}
      {edges.right && (
        <>
          <span className="pointer-events-none absolute inset-y-0 right-0 z-10 w-[60px] bg-gradient-to-r from-transparent to-white" />
          <button
            type="button"
            aria-label={t("valueChain.scrollLater")}
            onClick={() => scrollBy(1)}
            className={NAV_BUTTON}
            style={{ right: 6 }}
          >
            <ChevronRight className="h-4 w-4 text-innoveraZinc-900" />
          </button>
        </>
      )}
      <div ref={scrollerRef} onScroll={syncEdges} className="min-w-0 overflow-x-auto overflow-y-hidden">
        <div className="flex w-max min-w-full flex-col">
          <div className="flex items-center gap-2 self-stretch">
            <FigmaTypography variant="caption-1" color="zinc500">
              {t("valueChain.upstream")}
            </FigmaTypography>
            <span className="h-px flex-1 bg-innoveraZinc-150" />
            <FigmaTypography variant="caption-1" color="zinc500">
              {t("valueChain.downstream")}
            </FigmaTypography>
          </div>

          <div ref={cardsRowRef} className="flex items-end gap-3 px-px pt-[34px]">
            {preStages.length > 0 && (
              <div className="flex gap-3 py-3">{preStages.map((stage, i) => renderCard(stage, i, false))}</div>
            )}
            {baseSpan && (
              <div className="flex flex-col gap-3 rounded border border-innoveraZinc-150 bg-innoveraZinc-100 p-3">
                <div className="flex items-center justify-center gap-1">
                  <FigmaTypography variant="label-1" color="zinc900">
                    {t(POSITION_KEY.base)}
                  </FigmaTypography>
                  {base?.note && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={t("valueChain.aboutPosition", { label: base.label })}
                          className="shrink-0 text-innoveraZinc-500"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-[260px] text-sm leading-[22px] text-innoveraZinc-700">
                        {base.note}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="flex gap-2">
                  {baseStages.map((stage, j) => renderCard(stage, (baseSpan?.from ?? 0) + j, true))}
                </div>
              </div>
            )}
            {postStages.length > 0 && (
              <div className="flex gap-3 py-3">
                {postStages.map((stage, j) => renderCard(stage, (baseSpan ? baseSpan.to + 1 : 0) + j, false))}
              </div>
            )}
          </div>

          {!!ruleRows.length && stagePx.length === total && (
            <div className="flex flex-col gap-4 self-stretch px-px pb-4 pt-1">
              {ruleRows.map((row, rowIndex) => {
                const ordered = [...row].sort((a, b) => a.span.from - b.span.from);
                let cursor = 0;
                return (
                  <div key={rowIndex} className="flex items-start">
                    {ordered.map((entry, i) => {
                      const from = stagePx[entry.span.from];
                      const to = stagePx[entry.span.to];
                      const width = to.left + to.width - from.left;
                      const marginLeft = from.left - cursor;
                      cursor = from.left + width;
                      return (
                        <div key={i} className="shrink-0" style={{ marginLeft }}>
                          <PositionRule position={entry.position} width={width} />
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function WhoHoldsSection({ chain }: { chain: TValueChain }) {
  const { t } = useTranslation("projectHome");
  const [open, setOpen] = useState(false);
  const total = chain.stages.length;

  return (
    <section className="flex min-w-0 flex-col gap-4">
      <button
        type="button"
        aria-expanded={open}
        aria-label={open ? t("sidebar.collapse") : t("sidebar.expand")}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 text-left"
      >
        <FigmaTypography variant="heading-4" color="zinc900">
          {t("valueChain.whoHoldsTitle")}
        </FigmaTypography>
        <CaretUpIcon
          weight="regular"
          className={cn("size-[18px] shrink-0 text-innoveraZinc-900 transition-transform", !open && "rotate-180")}
        />
      </button>

      {open && (
        <div className="overflow-x-auto border border-innoveraZinc-150">
          <div className="min-w-[640px]">
            <div className="flex items-center gap-4 border-b border-innoveraZinc-150 bg-innoveraZinc-50 p-3">
              <FigmaTypography variant="caption-1" color="zinc500" className="w-[170px] shrink-0">
                {t("valueChain.colStage")}
              </FigmaTypography>
              <FigmaTypography variant="caption-1" color="zinc500" className="flex-1">
                {t("valueChain.colWhoHolds")}
              </FigmaTypography>
              <FigmaTypography variant="caption-1" color="zinc500" className="flex-1">
                {t("valueChain.colCharacterizes")}
              </FigmaTypography>
            </div>

            {chain.stages.map((stage, i) => (
              <div key={stage.key} className="flex gap-[26px] border-b border-innoveraZinc-150 px-3 py-4 last:border-0">
                <div className="flex w-[170px] shrink-0 flex-col gap-2">
                  <FigmaTypography
                    variant="label-3"
                    className={"text-innoveraZinc-400"}
                  >
                    {`${i + 1}/${total}`}
                  </FigmaTypography>
                  <FigmaTypography variant="heading-5" className="w-40 text-black">
                    {stage.label}
                  </FigmaTypography>
                </div>

                <div className="flex flex-1 flex-col gap-2">
                  <span className="inline-flex w-fit items-center rounded-2xl border border-innoveraZinc-150 px-2.5 py-1">
                    <FigmaTypography variant="label-3" color="zinc900">
                      {t(OWNER_KEY[stage.owner])}
                    </FigmaTypography>
                  </span>
                  {stage.heldBy && (
                    <FigmaTypography variant="body-2" color="zinc900">
                      {stage.heldBy}
                    </FigmaTypography>
                  )}
                </div>

                <div className="flex flex-1 flex-col gap-2">
                  {(stage.annotations ?? []).length === 0 && (
                    <FigmaTypography variant="body-2" color="zinc300">
                      —
                    </FigmaTypography>
                  )}
                  {(stage.annotations ?? []).map((annotation, ai) => (
                    <FigmaTypography key={ai} variant="body-2" color="zinc900">
                      <span className="font-medium">{`${t(ANNOTATION_KEY[annotation.kind])}.`}</span>{" "}
                      {sentenceCase(annotation.text)}
                    </FigmaTypography>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
